import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Radio, Calendar, Music, X, Heart, Menu, Mic, Upload } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_LINKS = [
  { path: '/', label: 'Home', key: 'home', icon: Radio },
  { path: '/schedule', label: 'Schedule', key: 'schedule', icon: Calendar },
  { path: '/shows', label: 'Shows', key: 'shows', icon: Music },
  { path: '/catchup', label: 'Catch Up', key: 'catchup', icon: Mic },
  { path: '/dedications', label: 'Dedications', key: 'dedications', icon: Heart },
  { path: '/submit-music', label: 'Submit Music', key: 'submit', icon: Upload },
];

export default function Header() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuSettings, setMenuSettings] = useState(null);

  const isAdmin = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (isAdmin) return;
    axios.get(`${API}/settings/menu`).then(r => setMenuSettings(r.data.items)).catch(() => {});
  }, [isAdmin]);

  if (isAdmin) return null;

  const links = menuSettings
    ? ALL_LINKS.filter(l => menuSettings[l.key] !== false)
    : ALL_LINKS;

  return (
    <header data-testid="main-header" className="sticky top-0 w-full backdrop-blur-xl bg-[#0F1115]/80 border-b border-white/5 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5">
          <img
            src="/favicon.png"
            alt="Christovibes"
            className="w-9 h-9 rounded-full ring-1 ring-white/10"
          />
          <span className="text-lg font-semibold text-white tracking-tight" style={{ fontFamily: 'Clash Display, sans-serif' }}>Christovibes</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {links.map(link => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.key}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                location.pathname === link.path
                  ? 'bg-white/10 text-white'
                  : 'text-[#94A3B8] hover:text-white hover:bg-white/5'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/admin"
            data-testid="nav-admin"
            className="ml-4 px-4 py-2 rounded-full text-sm font-medium text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 transition-colors"
          >
            Admin
          </Link>
        </nav>

        {/* Mobile Menu Toggle */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden p-2 rounded-lg hover:bg-white/10 text-white"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/5 bg-[#0F1115]/95 backdrop-blur-xl">
          <nav className="px-4 py-4 space-y-1">
            {links.map(link => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'bg-white/10 text-white'
                    : 'text-[#94A3B8] hover:text-white hover:bg-white/5'
                }`}
              >
                <link.icon size={18} /> {link.label}
              </Link>
            ))}
            <Link
              to="/admin"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-[#D4AF37] hover:bg-[#D4AF37]/10"
            >
              Admin Panel
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
